import {
  useCallback,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from "react";

export type AutoResizeTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** Minimum height in text rows (default 2). */
  minRows?: number;
  /** Stop growing after this many rows; scroll inside the field beyond that. */
  maxRows?: number;
};

export function AutoResizeTextarea({
  minRows = 2,
  maxRows,
  value,
  className,
  onChange,
  ...rest
}: AutoResizeTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const syncHeight = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    const styles = getComputedStyle(el);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const borderY = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const minHeight = lineHeight * minRows + padY + borderY;
    let next = Math.max(el.scrollHeight, minHeight);
    if (maxRows != null) {
      const maxHeight = lineHeight * maxRows + padY + borderY;
      if (el.scrollHeight > maxHeight) {
        next = maxHeight;
        el.style.overflowY = "auto";
      } else {
        el.style.overflowY = "hidden";
      }
    } else {
      el.style.overflowY = "hidden";
    }
    el.style.height = `${next}px`;
  }, [minRows, maxRows]);

  useLayoutEffect(() => {
    syncHeight();
  }, [value, syncHeight, className]);

  const mergedClassName = ["auto-resize-textarea", className].filter(Boolean).join(" ");

  return (
    <textarea
      ref={ref}
      rows={minRows}
      className={mergedClassName}
      value={value}
      onChange={(ev) => {
        onChange?.(ev);
        requestAnimationFrame(syncHeight);
      }}
      {...rest}
    />
  );
}
